import { getAllComponents } from '../components/registry';
import { ComponentDef } from '../components/types';
import { getState, navigateTo, setSearchOpen, subscribe } from '../state';

export function initSearchModal(container: HTMLElement): void {
  const overlay = document.createElement('div');
  overlay.className = 'search-modal';
  overlay.style.display = 'none';
  container.appendChild(overlay);

  let selectedIndex = 0;
  let results: ComponentDef[] = [];

  function render(): void {
    const open = getState().searchOpen;
    overlay.style.display = open ? 'flex' : 'none';
    if (!open) return;

    overlay.innerHTML = '';
    const modal = document.createElement('div');
    modal.className = 'search-modal-content';

    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Type to search components...';
    input.className = 'search-modal-input';
    input.autofocus = true;

    const list = document.createElement('ul');
    list.className = 'search-results';

    const query = new URLSearchParams(window.location.hash.split('?')[1] ?? '').toString();

    function updateResults(): void {
      const term = input.value.trim().toLowerCase();
      results = getAllComponents().filter(
        (c) =>
          c.name.toLowerCase().includes(term) ||
          c.description.toLowerCase().includes(term) ||
          c.id.toLowerCase().includes(term)
      );
      selectedIndex = 0;
      renderList();
    }

    function renderList(): void {
      list.innerHTML = '';
      for (let i = 0; i < results.length; i++) {
        const c = results[i];
        const li = document.createElement('li');
        li.className = i === selectedIndex ? 'selected' : '';
        li.textContent = `${c.name} — ${c.description}`;
        li.addEventListener('click', () => selectComponent(c, query));
        list.appendChild(li);
      }
      if (results.length === 0) {
        const empty = document.createElement('li');
        empty.className = 'empty';
        empty.textContent = 'No components found';
        list.appendChild(empty);
      }
    }

    function selectComponent(c: ComponentDef, queryStr: string): void {
      const path = `#/component/${c.id}`;
      navigateTo(queryStr ? `${path}?${queryStr}` : path);
      setSearchOpen(false);
    }

    input.addEventListener('input', updateResults);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        selectedIndex = Math.min(selectedIndex + 1, results.length - 1);
        renderList();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        selectedIndex = Math.max(selectedIndex - 1, 0);
        renderList();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (results[selectedIndex]) {
          selectComponent(results[selectedIndex], query);
        }
      } else if (e.key === 'Escape') {
        setSearchOpen(false);
      }
    });

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) setSearchOpen(false);
    });

    modal.appendChild(input);
    modal.appendChild(list);
    overlay.appendChild(modal);

    updateResults();
    input.focus();
  }

  subscribe(render);
  render();

  document.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      setSearchOpen(!getState().searchOpen);
    } else if (e.key === 'Escape' && getState().searchOpen) {
      setSearchOpen(false);
    }
  });
}
